/**
 * <TutorReleaseStudentCard> — ce que la libération fait à un élève (organism).
 *
 * Legacy `ReleaseSlotsSteps` → `StudentCard` : une carte par élève (ou par essai, stage, rattrapage
 * d'un élève de passage), dans l'ordre que le serveur a décidé — le prénom et la chip de ce qui lui
 * arrive (« reste avec toi », « risque de perte », « Élève perdu »…), la ligne d'alerte d'un élève à
 * risque, ses lignes dans l'ordre de lecture, et — quand il peut partir — les créneaux de sa famille
 * que le tuteur peut ouvrir pour le garder. Ouvrir un créneau redemande l'aperçu : c'est le seul
 * levier du tuteur sur ce qui arrive à un élève ; les liens tournent pendant que l'aperçu revient.
 *
 * Le ton arrive en donnée (`tone`) : la chip le peint, la ligne d'alerte l'écrit dans son encre.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Divider } from "../../atoms/Divider"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import {
    type UITutorReleaseCard,
    type UITutorReleaseSuggestedSlot,
    type UITutorReleaseTone,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

const TONE: Record<UITutorReleaseTone, { badge: BadgeVariant; ink: SemanticColorKey }> = {
    stays: { badge: "success", ink: "text-success" },
    atRisk: { badge: "warning", ink: "text-warning" },
    lost: { badge: "danger", ink: "text-danger" },
}

export type TutorReleaseStudentCardProps = {
    card: UITutorReleaseCard
    onOpenSlot?: (slot: UITutorReleaseSuggestedSlot) => void
    /** L'aperçu repart : les liens « Ajouter le… » tournent. */
    opening?: boolean
    testID?: string
}

export function TutorReleaseStudentCard(props: TutorReleaseStudentCardProps) {
    const { card, onOpenSlot, opening = false, testID } = props
    const tone = TONE[card.tone]
    const slots = card.suggestedSlots ?? []

    return (
        <Card p="4" testID={testID}>
            <Stack gap="2">
                <Row gap="2" align="start" justify="between">
                    <Text size="text-md" weight="bold" style={styles.name}>
                        {card.name}
                    </Text>
                    <Badge text={card.tag} variant={tone.badge} tinted truncate={false} />
                </Row>
                {card.alert != null && card.alert !== "" && (
                    <Text size="text-md" weight="bold" color={tone.ink}>
                        {card.alert}
                    </Text>
                )}
                {card.lines.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={index} size="text-md" color="text-muted">
                        {line}
                    </Text>
                ))}
                {card.suggestion != null && card.suggestion !== "" && (
                    <>
                        <Divider spacing="1" />
                        <Text size="text-md">{card.suggestion}</Text>
                        <View style={styles.slots}>
                            {slots.map((slot) => (
                                <TextButton
                                    key={slot.id}
                                    label={`Ajouter le ${slot.label}`}
                                    size="sm"
                                    onPress={() => onOpenSlot?.(slot)}
                                    loading={opening}
                                />
                            ))}
                        </View>
                    </>
                )}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    name: { flex: 1, flexShrink: 1 },
    slots: { flexDirection: "row", flexWrap: "wrap", columnGap: theme.spacing["4"], rowGap: theme.spacing["1"] },
}))
